import {
  AssociateSiteVisitQueueRow,
  SiteVisitView,
} from '../services/site-visit.service';
import {
  SiteVisitActorContext,
  SiteVisitTransition,
  canAssociate,
  pendingAssociateTransitionsFor,
} from '../site-visit/site-visit-state.util';
import { buildTestId } from '../shared/testid/testid.util';

/**
 * The scheduling queue's derivation, kept out of the component so it can be read and tested as a
 * table rather than through a fixture.
 *
 * 🔴 **Everything here is derived from `site-visit-state.util.ts`, which is the port of
 * connect-service's `SiteVisitTransitionEnum`.** There is no second state → group map and no second
 * set of labels in this feature, and that is a requirement rather than tidiness: the queue and the
 * per-project site-visit area both perform the same transitions (addendum §5.1 chose *supplement*, not
 * *replace*), so a state must not read one way here and another way there. A future state change lands
 * in the transition table and both surfaces follow it.
 */

/** The three groups, ordered by who owes the next move. */
export type SchedulingQueueGroupKey =
  | 'AWAITING_YOU'
  | 'WAITING_ON_OTHERS'
  | 'CONFIRMED';

/**
 * One row as a template renders it: the wire row plus what was derived from it.
 *
 * Declared here rather than on the page, because the row is rendered by
 * `SchedulingQueueRowComponent` and consumed by more than one surface. Keeping it on the page would make
 * the row component import the page it is rendered by — a cycle, and a claim that the queue is the page's
 * shape rather than the feature's.
 */
export interface SchedulingQueueRowVm {
  row: AssociateSiteVisitQueueRow;
  group: SchedulingQueueGroupKey;
  pending: SiteVisitTransition[];
  /**
   * The associate's own action drove this visit to a terminal state, so it is no longer live work and the
   * next queue read will not return it. It stays where it is until then — see
   * `SchedulingPageComponent.commit`.
   */
  settled: boolean;
  testId: string;
}

/** One group as the scheduling page renders it. */
export interface SchedulingQueueGroupVm {
  key: SchedulingQueueGroupKey;
  title: string;
  hint: string;
  testId: string;
  /** Live rows only — a settled row keeps its place but is no longer work, so it is not counted. */
  count: number;
  rows: SchedulingQueueRowVm[];
}

/** Render order. *Awaiting you* is first because "what needs me" is the point of the page. */
export const SCHEDULING_QUEUE_GROUP_ORDER: SchedulingQueueGroupKey[] = [
  'AWAITING_YOU',
  'WAITING_ON_OTHERS',
  'CONFIRMED',
];

/** Group headings. Title Case, matching the section headings elsewhere in the app. */
export const SCHEDULING_QUEUE_GROUP_TITLE: Record<SchedulingQueueGroupKey, string> = {
  AWAITING_YOU: 'Awaiting You',
  WAITING_ON_OTHERS: 'Waiting On Others',
  CONFIRMED: 'Confirmed',
};

/**
 * One line of explanation per group, so the ordering is legible rather than something to infer.
 *
 * 🔴 A hint describes **how a row got into this group**, never what is true of it now. A settled row —
 * one the PA completed or cancelled in this session — deliberately keeps its original group (see
 * `SchedulingPageComponent.commit`), so a present-tense claim about every row under a heading is a claim
 * the row directly below it can contradict: "Day and arrival time are locked" sat above a visit the PA
 * had just cancelled, whose `parties` `reconcileRow` had already nulled. Past tense is what makes the
 * same sentence true of both.
 */
export const SCHEDULING_QUEUE_GROUP_HINT: Record<SchedulingQueueGroupKey, string> = {
  AWAITING_YOU: 'These visits reached a step where the next move is yours.',
  WAITING_ON_OTHERS: 'The next move belongs to the site contact, the project manager, or the system.',
  CONFIRMED:
    'These visits reached an agreed day and arrival time. Site contact details appear on a row when there are any.',
};

/**
 * The one flag the resolving actor rules read, lifted off a row.
 *
 * 🔴 **`timeSuggestedByContact` dropped, not sourced from `proposedBy`** (Task 10) — see
 * `AssociateSiteVisitQueueRow`'s own doc comment. With no writer left, the member came off
 * `SiteVisitActorContext` entirely (Task 8), so `routedToPm` is the whole context now and this
 * function returns all of it.
 */
export function actorContextOf(
  row: Pick<AssociateSiteVisitQueueRow, 'routedToPm'>
): SiteVisitActorContext {
  return {
    routedToPm: !!row.routedToPm,
  };
}

/**
 * Every move the negotiation is **blocked on** — the pending set, not the affordance set.
 *
 * 🔴 `associateTransitionsFor` is the wrong function here and is the trap this queue was warned about:
 * it includes `COMPLETE` and `CANCEL`, which are `ASSOCIATE_OR_SYSTEM` and legal from nearly every
 * state, so grouping on it would put almost every visit under *Awaiting you* and the page would stop
 * meaning anything. The filter to `ASSOCIATE_ONLY` lives in the state util beside the affordance set —
 * see {@link pendingAssociateTransitionsFor} — and deliberately not here, so the two cannot drift.
 */
export function pendingActionsFor(row: AssociateSiteVisitQueueRow): SiteVisitTransition[] {
  return pendingAssociateTransitionsFor(row?.status, actorContextOf(row ?? {}));
}

/**
 * Which group a row belongs to.
 *
 * `TIME_CONFIRMED` is checked first and by name: the day and the arrival time are both settled, so it
 * is not "waiting on" anyone even though its pending set is empty — and it is the one group where the
 * site contact's details are on the row. Everything else is decided by the pending set, so the ten
 * live states land where the approved layout puts them without a state list being maintained here.
 */
export function queueGroupOf(row: AssociateSiteVisitQueueRow): SchedulingQueueGroupKey {
  if (row?.status === 'TIME_CONFIRMED') {
    return 'CONFIRMED';
  }
  return pendingActionsFor(row).length > 0 ? 'AWAITING_YOU' : 'WAITING_ON_OTHERS';
}

/**
 * Button copy for the associate's own moves — the four `ASSOCIATE_ONLY` moves the negotiation can be
 * *blocked on*, plus `RESCHEDULE`, which is `ASSOCIATE_ONLY` too but deliberately outside the pending
 * set (see `NON_BLOCKING_ASSOCIATE_TRANSITIONS`) and so is read by {@link canRescheduleRow}'s control
 * rather than by the pending-action button. Title Case, per the design system's button rule.
 *
 * A row renders ONE inline action regardless of how many moves are pending, because every one of them
 * does the same thing from the row — it opens the negotiation panel. `PENDING_PA_APPROVAL` is the state
 * that offers two (accepting the site contact's counter, or countering back with a different day and
 * time), and that choice is real; it is simply made in the panel, one click in, rather than duplicated
 * onto the row as two buttons wired to the same handler. Multi-move rows use
 * {@link PENDING_MULTI_ACTION_LABEL} instead of any entry here — see
 * `SchedulingPageComponent.actionLabel`.
 */
export const PENDING_ACTION_LABEL: Partial<Record<SiteVisitTransition, string>> = {
  PROPOSE_VISIT: 'Propose Visit',
  PA_ACCEPT: 'Accept',
  PA_COUNTER: 'Propose Another Time',
  RESCHEDULE: 'Reschedule',
};

/**
 * May the associate move this visit's agreed day?
 *
 * 🔴 Asked of the ported transition table, not of a status list kept here — `RESCHEDULE`'s from-set is
 * `SiteVisitTransitionEnum`'s and lives in one place. The row reads this rather than the pending set
 * because the reschedule is deliberately **not** a pending move: it is legal at four of the ten live
 * states, and counting it as pending work would sweep almost every healthy visit into *Awaiting you*.
 * So the queue asks two questions of the same table — "what is this blocked on" for the grouping, and
 * "what may the PA do" for this one affordance.
 */
export function canRescheduleRow(row: AssociateSiteVisitQueueRow): boolean {
  return canAssociate('RESCHEDULE', row?.status, actorContextOf(row ?? {}));
}

/**
 * The label when more than one move is pending. Named for what the button does — opens the panel — since
 * naming either move would name the wrong one half the time.
 */
export const PENDING_MULTI_ACTION_LABEL = 'Respond';

/** The label for a pending move, degrading to the transition name rather than throwing. */
export function pendingActionLabel(transition: SiteVisitTransition): string {
  return PENDING_ACTION_LABEL[transition] ?? 'Open';
}

/**
 * The day the row is about: the agreed one once the day/window axis has closed, the proposed one
 * before that. The same choice connect-service makes when it anchors the rendered times, so the day a
 * row shows and the zone its times were rendered in cannot disagree.
 */
export function operativeDateOf(row: AssociateSiteVisitQueueRow): string | null {
  return row?.confirmedDate ?? row?.proposedDate ?? null;
}

/**
 * In-group ordering: **operative date ascending, undated rows last.**
 *
 * Soonest first is what a queue is for — the visit three days out is the one at risk. A row with no
 * date yet (`AWAITING_SITE_CONTACT_INFO`, and `NEEDS_AVAILABILITY` before a day is offered) has nothing
 * to sort on and would otherwise sort as epoch-zero and pin itself to the top of the first group, so it
 * sinks to the bottom of its own group instead. Ties fall back to the project reference, so the order
 * is stable across refetches rather than reflecting Mongo's read order.
 */
export function compareQueueRows(
  a: AssociateSiteVisitQueueRow,
  b: AssociateSiteVisitQueueRow
): number {
  const aTime = timeValueOf(operativeDateOf(a));
  const bTime = timeValueOf(operativeDateOf(b));
  if (aTime !== bTime) {
    return aTime - bTime;
  }
  return (a?.projectOrderId ?? '').localeCompare(b?.projectOrderId ?? '');
}

/** A sortable value for a stored day, with absent/unparseable sorting last. */
function timeValueOf(rawDate: string | null | undefined): number {
  if (!rawDate) {
    return Number.POSITIVE_INFINITY;
  }
  const parsed = new Date(rawDate).getTime();
  return isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

/**
 * Fold a freshly-read detail view back onto the row that fetched it.
 *
 * 🔴 **This is what makes an opened row agree with what was opened.** The queue read is a list, taken
 * at some earlier moment; the detail read is the current negotiation, and the site contact or the
 * project manager may have answered in between. Folding the fresh view back is what stops the row
 * header claiming one state while the panel below it offers the moves of another. `parties` is the
 * clearest case: the queue row and the detail view are built by the same `partiesFor`, but only the
 * read that just happened can have the numbers this status actually discloses.
 *
 * 🔴 There is **no self-heal to fold back** — that claim used to live here and was wrong.
 * `SiteVisitOutreachHandler.systemAdvanceFor` now returns `null` for every status, so
 * `advanceStuckVisit` is a side-effect-free read and a visit parked in a retired state comes back in
 * the state it was already in. Opening a row does not advance it; it only tells the truth about it.
 *
 * The negotiation component's `(visitChange)` output is folded back through this same function, so
 * healing-on-open and in-place commit after the PA's own action are one path rather than two.
 *
 * Only the fields the row actually owns are taken, and `parties` is taken verbatim — both projections
 * are built by the detail view's own `partiesFor`, so there is one reveal gate and this side adds no
 * second status test that could come to disagree with it.
 */
export function reconcileRow(
  row: AssociateSiteVisitQueueRow,
  view: SiteVisitView | null | undefined
): AssociateSiteVisitQueueRow {
  if (!view) {
    return row;
  }
  return {
    ...row,
    status: view.status,
    propertyName: view.propertyName ?? row.propertyName,
    address: view.address ?? row.address,
    projectOrderId: view.projectOrderId ?? row.projectOrderId,
    proposedDate: view.proposedDate ?? null,
    confirmedDate: view.confirmedDate ?? null,
    routedToPm: !!view.routedToPm,
    displayProposedTime: view.displayProposedTime ?? null,
    displayConfirmedTime: view.displayConfirmedTime ?? null,
    parties: view.parties ?? null,
  };
}

/**
 * The view-model for one row, from the wire row it was read as.
 *
 * 🔴 **The one place a row view-model is built.** Every surface that renders
 * `SchedulingQueueRowComponent` derives `group`, `pending` and `testId` through here, so a row cannot
 * be grouped one way on the scheduling page and another way in the workspace's site-visit card — and
 * the workspace's two reads (the queue, and the site-scoped detail its fallback uses) cannot come to
 * disagree with each other either. `settled` is always `false`: it means "the associate's own action
 * in this session drove this visit out of the queue", which is never true of a row that was just read.
 *
 * The `scope` is the caller's render pass, so two rows whose composite id collides get the
 * convention's `-2` suffix rather than the same id — create a fresh one per build.
 */
export function buildQueueRowVm(
  row: AssociateSiteVisitQueueRow,
  scope: { next: (id: string) => string }
): SchedulingQueueRowVm {
  return {
    row,
    group: queueGroupOf(row),
    pending: pendingActionsFor(row),
    settled: false,
    testId: buildTestId(
      scope,
      'scheduling',
      row.projectOrderId || 'project',
      'site',
      String(row.siteNumber ?? '')
    ),
  };
}

/**
 * A queue row synthesized from the site-scoped detail read.
 *
 * 🔴 **Why this exists.** `GET /site-visit` (the queue) excludes `SITE_VISIT_COMPLETED` and
 * `CANCELLED` **in the query** — `SiteVisitAssociateHandler.LIVE_QUEUE_STATUSES`, and that exclusion is
 * deliberate: the queue drives the associate's cross-project work list, and finished work does not
 * belong in it. But `/project-history/:id` is the completed-project workspace, so its products are
 * exactly the two excluded states, and reading the queue alone would show a finished project an empty
 * card where its confirmed day and site contact used to be. The site-scoped read
 * (`SiteVisitAssociateHandler.forSite`) has no status filter and answers for a terminal visit, so the
 * card falls back to it and renders the SAME row from it. The queue is not widened; the workspace asks
 * a second, narrower question.
 *
 * 🔴 **`projectId` and `siteNumber` are seeded by the caller, because the detail projection does not
 * carry them** — it withholds both on the grounds that whoever called it put them in the path. They are
 * not cosmetic: they are the key the row's own detail fetch uses when the row is opened, so a row
 * synthesized without them renders correctly and then fails the moment someone expands it.
 *
 * Everything else is {@link reconcileRow}'s existing field mapping, called rather than repeated — this
 * is the same fold-back that heals a parked visit on open, and `parties` passes through it verbatim so
 * connect-service keeps its single reveal gate.
 */
export function rowFromDetailView(
  view: SiteVisitView,
  projectId: string,
  siteNumber: number
): AssociateSiteVisitQueueRow {
  return reconcileRow(
    { id: view.id, status: view.status, projectId, siteNumber },
    view
  );
}
